import { Component, ViewChildren, QueryList, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { filter, first, Observable } from "rxjs";
import { Store } from "@ngrx/store";

import { UtilsComponent } from "./components/utils.component";
import { ToggleableComponent } from "./components/toggleable.component";
import { PdfViewerComponent } from "./print/pdf/pdf-viewer.component";
import { ConsultationProcess } from "./data/model";
import { ConsultationService } from "./services/consultation.service";
import { RootState } from "./store/root";
import { ValidationService } from "./services/validation.service";
import { ProductIndexService } from "./services/product-index.service";
import { SanitizerService } from "./services/sanitizer.service";
import { PersistenceService } from "./store/model/persistence.service";
import { ValidityState } from "./store/validity/state";
import { stateBuilder } from "./store/utils";
import { zbdGlobals } from "../globals";
import { NotificationService } from "./services/notification.service";
import { ResetStore } from "./store/reset-meta";
import { KeycloakService } from "./security";
import { ViewState, MainSectionInfo } from "./store/view/state";
import * as View from "./store/view/actions";
import { LoggerService } from "./services/logger.service";

@Component({
    selector: "main-app",
    templateUrl: "app.component.html",
    styles: [
        `
            h1 {
                margin-left: 0;
            }

            .main-menu-container {
                float: right;
                width: 24px;
            }

            .enlarged-content {
                margin-left: -20px;
                margin-right: -20px;
            }

            .error-box {
                min-height: 130px;
            }

            .error {
                padding-top: 1.5em;
                text-align: center;
                font-size: 24px;
            }

            .error-info {
                text-align: center;
                font-size: 16px;
            }

            .waiting,
            .error {
                padding: 1em;
                text-align: center;
                font-size: 160%;
            }

            .waiting .message {
                vertical-align: middle;
                padding-left: 0.5em;
            }

            .waiting > .spinning {
                vertical-align: middle;
                font-size: 32px;
            }

            .bold-underline {
                border: none;
                height: 2px;
                color: #dadada;
                background-color: #dadada;
            }
        `,
    ],
})
export class ZBDAppComponent extends UtilsComponent {
    @ViewChildren(ToggleableComponent)
    accordions: QueryList<ToggleableComponent>;
    @ViewChildren("pdfToggle") pdfToggles: QueryList<ToggleableComponent>;
    @ViewChildren(PdfViewerComponent) pdfViewers: QueryList<PdfViewerComponent>;

    state$: Observable<LocalState>;
    validity$: Observable<ValidityState>;
    protocolLoadFailed$: Observable<boolean>;
    pending$: Observable<boolean>;

    buildVersion = version.BUILD_VERSION;
    zbdGlobals = zbdGlobals;

    dokumentenvorschau: MainSectionInfo = {
        key: "document",
        label: "Dokumentenvorschau",
    };

    private logger = inject(LoggerService);

    constructor(
        public consultationService: ConsultationService,
        private route: ActivatedRoute,
        private store: Store<RootState>,
        private validation: ValidationService,
        private productIndex: ProductIndexService,
        private sanitizer: SanitizerService,
        private persistence: PersistenceService,
        private notify: NotificationService,
        private keycloakService: KeycloakService,
    ) {
        super();

        this.store.dispatch(new ResetStore());
        this.notify.clear();

        this.state$ = LocalState.create(store);
        this.validity$ = store
            .select((s) => s.validity)
            .pipe(filter((_) => !!_));
        this.protocolLoadFailed$ = store.select(
            (s) => s.view.protocolLoadFailed,
        );

        this.pending$ = persistence.isPending;

        this.register(
            this.route.params.subscribe((params) => {
                const cpId: number = parseInt(params["protocolID"]);
                this.logger.log(
                    "Loading consultation process with ID %d.",
                    cpId,
                );
                this.persistence.loadConsultationProcess(cpId);
            }),
        );

        this.register(
            this.state$.pipe(first()).subscribe((state) => {
                if (
                    state.cp.IsFinished &&
                    state.view.selectedMainSection === null
                ) {
                    this.store.dispatch(
                        View.Update.create(
                            "selectedMainSection",
                            this.dokumentenvorschau,
                        ),
                    );
                }
            }),
        );
    }

    onToggleableOpened(item: ToggleableComponent) {
        const openAccordions = this.accordions
            .toArray()
            .filter((accordion) => accordion !== item)
            .filter((accordion) => accordion.isOpen);

        openAccordions.forEach((element) => {
            element.close();
        });

        if (item === this.pdfToggles.first) {
            this.pdfViewers.first.createPdf();
        }
    }

    onRequestDocumentReload(cp: ConsultationProcess) {
        this.store.dispatch(new ResetStore());
        this.persistence.loadConsultationProcess(cp.Id);
    }

    isAdmin() {
        const token = this.keycloakService.getExistingParsedToken();
        const roles =
            token.realm_access && token.realm_access.roles
                ? token.realm_access.roles
                : [];
        return roles.includes("App_Beratungsdokumentation_Global");
    }
}

class LocalState {
    cp: ConsultationProcess;
    view: ViewState;

    static create(store: Store<RootState>): Observable<LocalState> {
        return stateBuilder(store, new LocalState())
            .apply("cp", (s) => s.model.ConsultationProcess)
            .apply("view", (s) => s.view).state$;
    }
}
